/**
 * lib/sessionStore.js -- Port de src/js/state.js (app/frontend).
 *
 * MISMO contrato exacto: setSession/clearSession/getSession/can/
 * buildRecordCtx/isTiendaProfile/etc. -- cualquier modulo (React o no)
 * lo importa igual que antes. La UNICA diferencia real con el original
 * es de mecanismo interno, no de API publica:
 *
 *   Original: `session` es un objeto que se MUTA in-place
 *   (`session.token = ...`). Nadie necesita saber cuando cambia porque
 *   quien lo lee vuelve a leer el DOM entero al re-renderizar a mano.
 *
 *   Aqui: cada set* REEMPLAZA la referencia completa (objeto nuevo) y
 *   avisa a quien esta suscrito (`notify()`). Es lo minimo necesario
 *   para que `useSession()` (via useSyncExternalStore) sepa cuando debe
 *   re-renderizar -- React compara referencias, no haria caso a una
 *   mutacion in-place del mismo objeto.
 *
 * api.js importa este archivo DIRECTO (no via el hook de React) para
 * leer el token/remember-token durante un fetch -- exactamente igual
 * que en el original, donde api.js tampoco sabe nada de "shell.js" ni
 * de como se pinta la UI.
 */
import { REMEMBER_TOKEN_KEY } from './config.js';

function emptySession() {
  return {
    token: null,
    usuario: null,
    perfil: null,
    nombre: '',
    apellido: '',
    accesoFormato: 'TODOS',
    temaPreferido: 'W2',
    temaLogin: 'W2',
    tiendaId: null,
    tiendaDescripcion: null,
    subgerenteId: null,
    gerenteId: null,
    proveedorId: null,
    proveedorDescripcion: null,
    permisos: [],
  };
}

let session = emptySession();
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener());
}

/** Usado por useSession() (useSyncExternalStore) -- ver state/useSession.js. */
export function subscribeSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSession(loginResult) {
  session = {
    token: loginResult.token,
    usuario: loginResult.usuario,
    perfil: loginResult.perfil,
    nombre: loginResult.nombre || '',
    apellido: loginResult.apellido || '',
    accesoFormato: loginResult.accesoFormato || 'TODOS',
    temaPreferido: loginResult.temaPreferido || 'W2',
    temaLogin: loginResult.temaLogin || 'W2',
    tiendaId: loginResult.tiendaId ?? null,
    tiendaDescripcion: loginResult.tiendaDescripcion ?? null,
    subgerenteId: loginResult.subgerenteId ?? null,
    gerenteId: loginResult.gerenteId ?? null,
    proveedorId: loginResult.proveedorId ?? null,
    proveedorDescripcion: loginResult.proveedorDescripcion ?? null,
    permisos: Array.isArray(loginResult.permisos) ? loginResult.permisos : [],
  };
  if (loginResult.rememberToken) {
    localStorage.setItem(REMEMBER_TOKEN_KEY, loginResult.rememberToken);
  }
  notify();
}

export function clearSession() {
  session = emptySession();
  localStorage.removeItem(REMEMBER_TOKEN_KEY);
  notify();
}

/** Actualiza campos sueltos sin pasar por un login/resume completo (p.
 * ej. account.js guarda `temaPreferido`/`temaLogin` tras un save exitoso,
 * sin volver a pedir sesion). Nuevo respecto al original SOLO porque ahi
 * `getSession().temaPreferido = next` mutaba directo -- aqui hace falta
 * un setter explicito para que `notify()` se dispare y los componentes
 * suscritos se enteren. Mismo contrato, mecanismo obligado por React. */
export function patchSession(fields) {
  session = { ...session, ...fields };
  notify();
}

export function getSession() {
  return session;
}

export function getSessionToken() {
  return session.token;
}

export function getRememberToken() {
  return localStorage.getItem(REMEMBER_TOKEN_KEY);
}

const SESSION_EXPIRED_FLAG = 'solicitudesbi_session_expired_notice';

export function markSessionExpiredNotice() {
  try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1'); } catch { /* noop */ }
}

export function consumeSessionExpiredNotice() {
  try {
    const had = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
    sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    return had;
  } catch {
    return false;
  }
}

export function isTiendaProfile() {
  return session.perfil === 'TIENDA';
}

export function isOmnicanalProfile() {
  return session.perfil === 'OMNICANAL';
}

export function isProveedorProfile() {
  return session.perfil === 'PROVEEDOR';
}

export function isLastMileProfile() {
  return session.perfil === 'SUBGERENTE_LAST_MILE' || session.perfil === 'GERENTE' || session.perfil === 'ADMINISTRADOR';
}

export function can(codigo) {
  return Array.isArray(session.permisos) && session.permisos.includes(codigo);
}

export function buildRecordCtx() {
  if (isTiendaProfile()) {
    return { mode: 'tienda', token: session.token, tiendaDescripcion: session.tiendaDescripcion };
  }
  return { mode: 'subgerente', token: session.token };
}
